/* eslint-disable no-unsafe-optional-chaining */
import { useReducer, useEffect } from 'react'
import _kebabCase from 'lodash/kebabCase.js'
import _get from 'lodash/get.js'
import { fetcher } from '../../productDetail/Helper.js'
import * as StringFormatter from '../../../../global/formatters/string-formatter.js'

export const lookupCategory = [
  'Domestic Equity',
  'Global/International Equity',
  'Sector Equity/Specialty',
  'Asset Allocation',
  'Alternative',
  'Taxable Fixed Income',
  'Tax-Exempt Fixed Income',
  'Money Market',
]

export const productTypes = {
  MF: 'Retail',
  ETF: 'RetailETF',
  MP: 'ModelPortfolios',
  FS: 'FutureScholar529',
  VP: 'VariableProducts',
  IF: 'IntervalFund',
  CE: 'ClosedEnd',
}

const ACTIONS = {
  UPDATE_DATA: 'UPDATE_DATA',
  SORT_DATA: 'SORT_DATA',
}

export const productsConfig = {
  [productTypes.MF]: {
    label: 'Mutual Fund',
    value: 'mutual-funds',
    group: 'retail',
    dataType: 'Monthly',
    defaultClass: 'Institutional',
    classOptions: [
      'A',
      'C',
      'Institutional',
      'Institutional2',
      'Institutional3',
      'R',
      'S',
    ],
    dataKey: 'mutualFunds.performanceFunds',
    asOfDateKey: 'mutualFunds.navAsOfDate',
    expenseAsOfDateKey: 'mutualFunds.expenseAsOfDate',
  },
  [productTypes.ETF]: {
    label: 'Exchange Traded Fund (ETF)',
    value: 'exchange-traded-funds',
    group: 'etf',
    dataType: 'Monthly',
    defaultClass: null,
    classOptions: [],
    dataKey: 'etfs.performanceFunds',
    asOfDateKey: 'etfs.navAsOfDate',
    expenseAsOfDateKey: 'etfs.expenseAsOfDate',
  },
  [productTypes.MP]: {
    label: 'Model Portfolio',
    value: 'model-portfolios',
    group: 'ModelPortfolios',
    dataType: null,
    defaultClass: null,
    classOptions: [],
    dataKey: 'modelPortfolios.portfolioReturns',
    asOfDateKey: 'modelPortfolios.portfolioReturns[0].asOfDate',
    expenseAsOfDateKey: null,
  },
  [productTypes.FS]: {
    label: '529 Plan',
    value: '529-plan',
    group: 'FutureScholar529',
    dataType: 'Monthly',
    defaultClass: 'A',
    classOptions: ['A', 'C', 'E', 'I'],
    dataKey: 'products',
    asOfDateKey: 'products[0].asOfDate',
    expenseAsOfDateKey: 'products[0].expenseAsOfDate',
  },
  [productTypes.CE]: {
    label: 'Listed Closed-End Fund',
    value: 'closed-end-funds',
    group: 'ClosedEnd',
    dataType: 'Monthly',
    defaultClass: null,
    classOptions: [],
    dataKey: 'closedEndFunds',
    asOfDateKey: 'closedEndFunds[0].asOfDate',
    expenseAsOfDateKey: null,
  },
  [productTypes.VP]: {
    label: 'Variable Product',
    value: 'variable-products',
    group: 'VariableProducts',
    dataType: 'Monthly',
    defaultClass: 'undefined',
    classOptions: [],
    dataKey: 'variableProducts',
    asOfDateKey: 'variableProducts[0].asOfDate',
    expenseAsOfDateKey: 'variableProducts[0].expenseAsOfDate',
  },
  [productTypes.IF]: {
    label: 'Interval Fund',
    value: 'interval-funds',
    group: 'interval',
    dataType: 'Monthly',
    defaultClass: null,
    classOptions: [],
    dataKey: 'intervalFunds.performanceFunds',
    asOfDateKey: 'intervalFunds.navAsOfDate',
    expenseAsOfDateKey: 'intervalFunds.expenseAsOfDate',
  },
}

const initState = {
  isLoading: true,
  searchInput: '',
  selected: {
    assetClass: [],
    productType: ['mutual-funds', 'exchange-traded-funds', 'interval-funds'],
    class: [],
  },
  selectedSort: 'fundName',
  selectedSortDirection: true,
  selectedSortType: 'string',
  allProductData: {},
}

const getProductData = async (
  group,
  dataType,
  className = null,
  dataCallback = null
) => {
  let url = `/pnp.svc/productSummary?group=${group}`
  if (dataType !== null) {
    url = `${url}&dataType=${dataType}`
  }
  if (className !== null) {
    url = `${url}&className=${className}`
  }
  const data = (await fetcher(url))?.value || {}
  dataCallback(data)
}

const getProductSummaryData = (product, callback) => {
  const {
    classOptions,
    group,
    dataType,
    defaultClass,
    dataKey,
    value,
    asOfDateKey,
    expenseAsOfDateKey,
  } = productsConfig[product]

  const dataManipulation = (data) => {
    const rows = _get(data, dataKey, [])
    const finalRows = rows.map((itm) => {
      const itms = { ...itm }
      if (
        value === productsConfig[productTypes.VP].value &&
        itms.className === ' '
      ) {
        itms.className = 'Shares'
      }
      return getProductRows(itms, productsConfig[product])
    })
    callback({
      [value]: {
        asOfDate: StringFormatter.formatDateStandard(
          _get(data, asOfDateKey, '-')
        ),
        expenseAsOfDate: StringFormatter.formatDateStandard(
          _get(data, expenseAsOfDateKey, '-')
        ),
        rows: finalRows,
        classOptions,
      },
    })
  }

  if (classOptions?.length > 0) {
    classOptions.forEach((itm) => {
      getProductData(group, dataType, itm, dataManipulation)
    })
  } else {
    getProductData(group, dataType, defaultClass, dataManipulation)
  }
}

function combineReducer(state, action) {
  switch (action.type) {
    case ACTIONS.UPDATE_DATA: {
      const data = action.payload
      const product = Object.keys(data)[0]
      const _state = {
        ...state,
        isLoading: false,
      }
      if (Object.keys(state.allProductData).includes(product)) {
        _state.allProductData[product] = {
          ...state.allProductData[product],
          rows: [...state.allProductData[product].rows, ...data[product].rows],
        }
      } else {
        _state.allProductData = {
          ...state.allProductData,
          ...action.payload,
        }
      }
      return _state
    }
    case ACTIONS.SORT_DATA: {
      return {
        ...state,
        ...action.payload,
      }
    }
    default:
      return state
  }
}

export function useAllProductTypesReducer() {
  const [state, dispatch] = useReducer(combineReducer, initState)
  const { selectedSort, selectedSortDirection } = state

  const initialLoad = () => {
    const otherProducts = Object.values(productTypes)
    for (let i = 0; i < otherProducts.length; i++) {
      getProductSummaryData(otherProducts[i], function (payload) {
        dispatch({
          type: ACTIONS.UPDATE_DATA,
          payload,
        })
      })
    }
  }

  const sortData = (payload = {}) => {
    dispatch({ type: ACTIONS.SORT_DATA, payload })
  }

  const onClickSort = (event) => {
    const sort = event.currentTarget.dataset.sort
    const sortType = event.currentTarget.dataset.sortType
    const sortDirection = !!parseInt(event.currentTarget.dataset.sortDirection)
    sortData({ selectedSortType: sortType })
    if (sort === selectedSort) {
      sortData({ selectedSortDirection: !selectedSortDirection })
    } else {
      sortData({
        selectedSort: sort,
        selectedSortDirection: sortDirection,
      })
    }
  }

  useEffect(() => {
    initialLoad()
  }, [])

  return {
    ...state,
    onClickSort,
    sortData,
    initialLoad,
  }
}

export const filterData = (selected, searchInput, allProductData) => {
  const count = Object.keys(selected).reduce(
    (p, n) => p + selected[n]?.length,
    0
  )
  const allProductKeys = Object.keys(allProductData)
  let result = allProductKeys.reduce(
    (p, n) => [...p, ...allProductData[n].rows],
    []
  )

  if (result?.length < 1) {
    return result
  }
  if (count > 0) {
    if (selected?.productType?.length > 0) {
      result = selected?.productType?.reduce(
        (p, n) =>
          allProductData[n] ? [...p, ...allProductData[n].rows] : [...p],
        []
      )
    }
    if (selected?.assetClass?.length > 0) {
      result = result?.filter((i) =>
        selected?.assetClass?.some((d) => d === _kebabCase(i?.categoryName))
      )
    }
    if (selected?.class?.length > 0) {
      result = result?.filter((i) =>
        selected?.class?.some((d) => d === _kebabCase(i?.fundClass))
      )
    }
  }

  if (searchInput?.length > 0) {
    result = result?.filter((i) =>
      `${i?.symbol} ${i?.fundName}`
        ?.toLowerCase()
        ?.includes(searchInput?.toLowerCase())
    )
  }

  return result
}

export const getUrlParams = (
  { classOptions, assertsOptions, productOptions },
  sortData,
  urlParams
) => {
  if (
    urlParams.has('productType') ||
    urlParams.has('assetClass') ||
    urlParams.has('class')
  ) {
    const hasValueInArray = (arr, val) => arr.some((i) => i.value === val)

    let _product = [],
      _asserts = [],
      _class = []
    if (urlParams.has('productType')) {
      _product = urlParams
        ?.get('productType')
        ?.split(',')
        ?.filter((i) => hasValueInArray(productOptions, i))
    }

    if (urlParams.has('assetClass')) {
      _asserts = urlParams
        ?.get('assetClass')
        ?.split(',')
        ?.filter((i) => hasValueInArray(assertsOptions, i))
    }

    if (urlParams.has('class')) {
      _class = urlParams
        ?.get('class')
        ?.split(',')
        ?.filter((i) => hasValueInArray(classOptions, i))
    }
    sortData({
      selected: {
        assetClass: _asserts,
        productType: _product,
        class: _class,
      },
    })
  }
}

export const updateUrlParams = (name, value) => {
  const url = new URL(window.location.href)
  const params = url?.searchParams
  if (value === undefined || value?.length < 1) {
    params?.delete(name)
  } else {
    const _val = value?.toString()
    params?.set(name, _val)
  }
  StringFormatter.safeReplaceState(url)
}

const getProductRows = (n, productInfo) => {
  const { value, label } = productInfo
  const obj = {
    productType: value,
    productLabel: label,
    fundClass: n?.fundClass || n?.className || '-',
    fundName: n?.fundName || n?.name || '-',
    ytdPercent: n?.ytdPercent || n?.ytd || '-',
    msCategory: n?.msCategory || '-',
    categoryName: n?.categoryName || '-',
    cusip: n?.cusip || n?.accountID || '-',
    symbol: n?.symbol || '-',
    oneYear: n?.navOneYear || n?.oneYear || n?.puvOneYear || '-',
    fiveYear: n?.navFiveYear || n?.fiveYear || n?.puvFiveYear || '-',
    tenYear: n?.navTenYear || n?.tenYear || n?.puvTenYear || '-',
    sinceInception:
      n?.navSinceInception || n?.sinceIncep || n?.puvSinceInception || '-',
    waiverDate: n?.expenseRatioWaiverDate
      ? StringFormatter.formatDateStandard(n?.expenseRatioWaiverDate)
      : '-',
    netExp: n?.netExp || '-',
    grossExp: n?.grossExp || '-',
  }

  return obj
}

export const getTableHeaderDates = (selected, allProductData) => {
  const MF = productsConfig[productTypes.MF].value
  const dates = {
    asOfDate: allProductData[MF]?.asOfDate,
    expenseAsOfDate: allProductData[MF]?.expenseAsOfDate,
  }

  if (
    selected?.productType?.length > 0 &&
    !selected?.productType.includes(MF)
  ) {
    const firstSelect = selected?.productType[0]
    dates.asOfDate = allProductData[firstSelect]?.asOfDate
    dates.expenseAsOfDate =
      allProductData[firstSelect]?.expenseAsOfDate !== '-'
        ? allProductData[firstSelect]?.expenseAsOfDate
        : ''
  }

  return dates
}
